import {
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import {
  decompose,
  type Salt,
  saltFormula,
} from "@/lib/tool-kit/chem-reference/ions";
import {
  findBySalt,
  formulaMass,
  pureWaterSolubility,
} from "@/lib/tool-kit/chem-reference/ksp";
import {
  classifySalt,
  type NoteId,
  type RuleId,
  type Status,
} from "@/lib/tool-kit/chem-reference/solubility-rules";
import {
  formatSig,
  type NumberFormatter,
  superscriptInt,
} from "@/lib/tool-kit/chemistry/format";
import { parseFormula } from "@/lib/tool-kit/chemistry/formula";

export interface CheckStrings {
  statusNames: Record<Status, string>;
  verdict: string;
  ionsLabel: string;
  ionsLine: string;
  ruleLabel: string;
  ruleText: Record<RuleId, string>;
  exceptionLine: string;
  noteText: Record<NoteId, string>;
  equilibriumLine: string;
  kspExpression: string;
  tabulatedKsp: string;
  tabulatedSolubility: string;
  noKsp: string;
  notSalt: string;
  emptyPrompt: string;
  formulaError: string;
}

const MOLAR_SIG = 2;
const STATE_SUFFIX = /\s*\((?:aq|s|l|g)\)\s*$/;

/** "[Ba²⁺][SO₄²⁻]" with the stoichiometric powers: [Ca²⁺][F⁻]². */
export function kspExpression(salt: Salt): string {
  const power = (n: number) => (n === 1 ? "" : superscriptInt(n));
  return `[${salt.cation.text}]${power(salt.nCation)}[${salt.anion.text}]${power(salt.nAnion)}`;
}

function dissociation(salt: Salt, formula: string, arrow: string): string {
  const count = (n: number) => (n === 1 ? "" : `${n} `);
  return `${formula} ${arrow} ${count(salt.nCation)}${salt.cation.text}(aq) + ${count(salt.nAnion)}${salt.anion.text}(aq)`;
}

function KspBlock({
  salt,
  strings,
  fmt,
}: {
  salt: Salt;
  strings: CheckStrings;
  fmt: NumberFormatter;
}) {
  const entry = findBySalt(salt);
  if (!entry) {
    return <Notice>{strings.noKsp}</Notice>;
  }
  const s = pureWaterSolubility(entry.ksp, salt.nCation, salt.nAnion);
  const mass = formulaMass(entry.formula);
  return (
    <div className={WORKING_BOX}>
      <p>
        {t(strings.tabulatedKsp, {
          formula: entry.text,
          ksp: formatSig(fmt, entry.ksp, MOLAR_SIG),
        })}
      </p>
      <p>
        {t(strings.tabulatedSolubility, {
          molar: formatSig(fmt, s, MOLAR_SIG),
          grams: mass === null ? "—" : formatSig(fmt, s * mass, MOLAR_SIG),
        })}
      </p>
    </div>
  );
}

export function CheckResults({
  formula,
  strings,
  fmt,
}: {
  formula: string;
  strings: CheckStrings;
  fmt: NumberFormatter;
}) {
  if (formula.trim() === "") {
    return <Notice>{strings.emptyPrompt}</Notice>;
  }
  if (!parseFormula(formula.replace(STATE_SUFFIX, "")).ok) {
    return <ErrorNotice>{strings.formulaError}</ErrorNotice>;
  }
  const salt = decompose(formula);
  if (!salt) {
    return <ErrorNotice>{strings.notSalt}</ErrorNotice>;
  }
  const verdict = classifySalt(salt);
  const name = saltFormula(salt);
  const soluble = verdict.status === "soluble";
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.verdict, { formula: name })}
          value={strings.statusNames[verdict.status]}
        />
        <StatCard
          label={strings.ionsLabel}
          value={t(strings.ionsLine, {
            n: String(salt.nCation),
            cation: salt.cation.text,
            m: String(salt.nAnion),
            anion: salt.anion.text,
          })}
        />
      </StatGrid>
      <div className="space-y-2 text-foreground text-sm">
        <p>
          <span className="font-medium">{strings.ruleLabel}: </span>
          {strings.ruleText[verdict.rule]}
        </p>
        {verdict.exception ? <p>{strings.exceptionLine}</p> : null}
        {verdict.note === "none" ? null : (
          <p>{strings.noteText[verdict.note]}</p>
        )}
      </div>
      <div className={WORKING_BOX}>
        <p>
          {soluble
            ? dissociation(salt, `${name}(s)`, "→")
            : t(strings.equilibriumLine, {
                equation: dissociation(salt, `${name}(s)`, "⇌"),
              })}
        </p>
        {soluble ? null : (
          <p>{t(strings.kspExpression, { expression: kspExpression(salt) })}</p>
        )}
      </div>
      {soluble ? null : <KspBlock fmt={fmt} salt={salt} strings={strings} />}
    </>
  );
}
